import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, MapPin, Tags, ClipboardList, Workflow,
  Banknote, FileText, BarChart3, Users, Building2, LogOut, Shield, Package, Settings, History, DatabaseBackup, KeyRound
} from "lucide-react";

const NAV = [
  { to: "/", label: "Panel", icon: LayoutDashboard, testId: "sidebar-nav-dashboard" },
  { to: "/koyler", label: "Köyler", icon: MapPin, testId: "sidebar-nav-villages" },
  { to: "/kategoriler", label: "İş Kategorileri", icon: Tags, testId: "sidebar-nav-categories" },
  { to: "/malzemeler", label: "Malzemeler", icon: Package, testId: "sidebar-nav-materials" },
  { to: "/calisma-kayitlari", label: "Çalışma Kayıtları", icon: ClipboardList, testId: "sidebar-nav-work-logs" },
  { to: "/altyapi", label: "Altyapı Takibi", icon: Workflow, testId: "sidebar-nav-infrastructure" },
  { to: "/yatirimlar", label: "Yatırım Takibi", icon: Banknote, testId: "sidebar-nav-investments" },
  { to: "/dilekceler", label: "Dilekçeler", icon: FileText, testId: "sidebar-nav-petitions" },
  { to: "/kurumlar", label: "Kurumlar", icon: Building2, testId: "sidebar-nav-institutions" },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3, testId: "sidebar-nav-reports" },
  { to: "/islem-gecmisi", label: "İşlem Geçmişi", icon: History, testId: "sidebar-nav-audit" },
  { to: "/yedekleme", label: "Yedekleme", icon: DatabaseBackup, testId: "sidebar-nav-backup" },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings, testId: "sidebar-nav-settings" },
];

export default function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  return (
    <aside className={`fixed top-0 left-0 z-40 h-screen w-64 bg-[#0F172A] text-slate-100 flex flex-col transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
           data-testid="main-sidebar" onClick={(e) => { if (e.target.closest("a")) onClose(); }}>
      <div className="px-6 py-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-emerald-600 flex items-center justify-center font-bold text-white">
            K
          </div>
          <div>
            <div className="font-display font-bold text-base leading-tight">KHGB</div>
            <div className="text-xs text-slate-400">Yönetim Sistemi</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={n.testId}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-emerald-600/15 text-emerald-300 border-l-2 border-emerald-500"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            <span>{n.label}</span>
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div className="mt-4 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Yönetim
            </div>
            <NavLink
              to="/kullanicilar"
              data-testid="sidebar-nav-users"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-emerald-600/15 text-emerald-300 border-l-2 border-emerald-500"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Users className="h-4 w-4" />
              <span>Kullanıcılar</span>
            </NavLink>
          </>
        )}
      </nav>

      <div className="px-3 py-4 border-t border-slate-800">
        <div className="flex items-center gap-3 px-3 pb-3">
          <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold">
            {user?.full_name?.[0] || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
              {user?.full_name}
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-1">
              {isAdmin ? <Shield className="h-3 w-3" /> : null}
              {isAdmin ? "Yönetici" : "Veri Giriş Personeli"}
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate("/sifre-degistir")}
          data-testid="sidebar-change-password-button"
          className="w-full flex items-center gap-2 justify-center px-3 py-2 mb-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
        >
          <KeyRound className="h-4 w-4" /> Şifremi Değiştir
        </button>
        <button
          onClick={logout}
          data-testid="sidebar-logout-button"
          className="w-full flex items-center gap-2 justify-center px-3 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors"
        >
          <LogOut className="h-4 w-4" /> Çıkış Yap
        </button>
      </div>
    </aside>
  );
}
